import * as THREE from "three";

export type VehicleMountPhase = "mounting" | "dismounting";

/** A fast exit: the rider shoves off and leaves the chassis behind on a
 * ballistic arc instead of stepping down onto a ground socket.
 *
 * The arc is expressed in CHASSIS-LOCAL space, and that is the whole trick.
 * Integrating it in world space would look right for a frame and then drift
 * metres: planets orbit and spin, so a world-fixed launch point slides out from
 * under a carried rider at surface speed. Relative to the chassis the rider's
 * separation is small and physical — inherited momentum is already in the
 * chassis frame — so the existing carry contract covers it for free.
 */
export interface VehicleBailProfile {
  /** Chassis-local up speed of the push-off (m/s). */
  lift: number;
  /** Chassis-local speed away from the seat along the exit side (m/s). */
  push: number;
  /** Local-up gravity used for the separation arc (m/s², positive down). */
  gravity: number;
}

export interface VehicleMountTransition {
  phase: VehicleMountPhase;
  /** Chassis-local boarding side picked from the rider's actual approach. */
  side: -1 | 1;
  elapsed: number;
  duration: number;
  approachFraction: number;
  arcHeight: number;
  startPosition: THREE.Vector3;
  startRotation: THREE.Quaternion;
  /** Present only on a fast exit. `phase` stays "dismounting" so seat, camera
   * and coast ownership keep the one exit path. */
  bail?: VehicleBailProfile;
}

function ease(value: number): number {
  const t = THREE.MathUtils.clamp(value, 0, 1);
  return t * t * (3 - 2 * t);
}

/**
 * Fraction of an exit over which the SEATED pose is given up.
 *
 * It is deliberately shorter than the exit itself, and not simply `1 - k`.
 * `applyMountedPose` IK-pins the hands to the grips and the boots to the pegs at
 * this weight, so a blend that only reaches zero on the final frame drags the
 * swinging leg back toward its peg through the entire step-over — the authored
 * kick reads at half amplitude for no visible reason. Releasing early hands the
 * body to the authored FK while the clip's OWN hand constraints still hold the
 * rider on the bars, which is the correct division: contact is animated, posture
 * is not.
 */
const DISMOUNT_RELEASE = 0.62;
/** Bail separation is one shove and then ballistic — release faster still. */
const BAIL_RELEASE = 0.45;
/**
 * Floor under an exit's mounted weight, held until the gameplay detach.
 *
 * `Avatar.setMountedBlend` treats anything at or below 0.001 as dismounted and
 * switches the legs over to terrain foot-grounding — which, mid-exit, solves
 * against the ground probe data the rider has not been updating since they got
 * on, and twitches the feet. The rider is still ON the vehicle until detach; a
 * weight this small poses nothing.
 */
const MOUNTED_HOLD = 0.002;

const _bailOffset = new THREE.Vector3();
const _bailTwist = new THREE.Quaternion();
const _bailAxis = new THREE.Vector3();

/**
 * Resolve the animated avatar root and mounted-IK blend for a seat transition.
 * Enter begins at the rider's captured visible root. It may lie on any side of
 * the vehicle; side-specific motion is animation/IK, not a required waypoint.
 */
export function vehicleMountTransitionPose(
  transition: VehicleMountTransition,
  entryPosition: THREE.Vector3,
  riderPosition: THREE.Vector3,
  vehicleRotation: THREE.Quaternion,
  up: THREE.Vector3,
  outPosition: THREE.Vector3,
  outRotation: THREE.Quaternion,
  renderElapsed = transition.elapsed,
): number {
  const progress = THREE.MathUtils.clamp(renderElapsed / Math.max(0.001, transition.duration), 0, 1);
  if (transition.phase === "mounting") {
    const k = ease(progress);
    outPosition.lerpVectors(entryPosition, riderPosition, k)
      .addScaledVector(up, Math.sin(Math.PI * k) * transition.arcHeight);
    outRotation.slerpQuaternions(transition.startRotation, vehicleRotation, k);
    return k;
  }

  if (transition.bail) {
    return vehicleBailPose(
      transition, transition.bail, riderPosition, vehicleRotation, outPosition, outRotation, renderElapsed,
    );
  }

  const k = ease(progress);
  outPosition.lerpVectors(riderPosition, entryPosition, k)
    .addScaledVector(up, Math.sin(Math.PI * k) * transition.arcHeight);
  outRotation.copy(vehicleRotation);
  return exitRelease(progress, DISMOUNT_RELEASE);
}

/** Seated weight remaining at `progress`, gone by `window` and floored until the
 * caller completes the exit. */
function exitRelease(progress: number, window: number): number {
  const released = ease(THREE.MathUtils.clamp(progress, 0, 1) / window);
  return Math.max(progress >= 1 ? 0 : MOUNTED_HOLD, 1 - released);
}

/**
 * Ballistic separation from a moving chassis, in the chassis' own frame.
 *
 * `seatPosition` and `vehicleRotation` are the LIVE interpolated chassis pose,
 * so the arc rides the vehicle's travel (and the planet's carry) exactly like
 * the step-off's socket tracking does — the offset it adds on top is the
 * rider's own separation and nothing else.
 */
export function vehicleBailPose(
  transition: VehicleMountTransition,
  bail: VehicleBailProfile,
  seatPosition: THREE.Vector3,
  vehicleRotation: THREE.Quaternion,
  outPosition: THREE.Vector3,
  outRotation: THREE.Quaternion,
  renderElapsed = transition.elapsed,
): number {
  const t = THREE.MathUtils.clamp(renderElapsed, 0, transition.duration);
  vehicleBailOffset(transition, bail, t, _bailOffset);
  outPosition.copy(_bailOffset).applyQuaternion(vehicleRotation).add(seatPosition);
  // Yaw off the chassis toward the exit side and tip backward as the legs come
  // up: the rider leaves facing where they were going, not where they land.
  const swing = ease(t / Math.max(0.001, transition.duration));
  _bailAxis.set(0, 1, 0);
  outRotation.copy(vehicleRotation)
    .multiply(_bailTwist.setFromAxisAngle(_bailAxis, transition.side * 0.62 * swing));
  _bailAxis.set(1, 0, 0);
  outRotation.multiply(_bailTwist.setFromAxisAngle(_bailAxis, -0.34 * swing));
  return exitRelease(t / Math.max(0.001, transition.duration), BAIL_RELEASE);
}

/** Chassis-local displacement of a bailing rider from the seat, at clip time. */
export function vehicleBailOffset(
  transition: VehicleMountTransition,
  bail: VehicleBailProfile,
  time: number,
  out: THREE.Vector3,
): THREE.Vector3 {
  const t = Math.max(0, time);
  return out.set(
    transition.side * bail.push * t,
    bail.lift * t - 0.5 * bail.gravity * t * t,
    0,
  );
}

/** Chassis-local velocity of a bailing rider at clip time. Detach adds this to
 * the chassis' own velocity so the handoff continues the same trajectory. */
export function vehicleBailVelocity(
  transition: VehicleMountTransition,
  bail: VehicleBailProfile,
  time: number,
  out: THREE.Vector3,
): THREE.Vector3 {
  const t = Math.max(0, time);
  return out.set(transition.side * bail.push, bail.lift - bail.gravity * t, 0);
}
